import * as naturalSort from 'javascript-natural-sort';
import { max } from 'underscore';

/**
 * @class ProductFamily
 */
export default class ProductFamily {
    constructor(variants) {
        this.variants = variants;
    }

    /**
     * Returns variant for given options.
     *
     * @param {number} size
     * @param {string} accessory
     * @param {string} align
     * @param {string} version
     * @return {Object} Variant
     */
    get(...args) {
        let item;
        const [size, accessory, align, version] = args;

        if (args.length > 3 && version !== undefined) {
            if (version.indexOf('_') >= 0) {
                item = this._getExactVersion(size, accessory, align, version);
            } else {
                item = this._getClosestVersion(size, accessory, align, version);
            }
        } else {
            item = this._getLatestVersion(size, accessory, align);
        }

        return item;
    }

    /**
     * Returns variant for given options.
     *
     * @param {number} size
     * @param {string} accessory
     * @param {string} align
     * @param {string} version
     * @return {Object} Variant
     */
    _getExactVersion(size, accessory, align, version) {
        this._assertExists(size, accessory, align, version);
        return this.variants[size][accessory][align][version];
    }

    /**
     * Returns variant for given options.
     *
     * @param {number} size
     * @param {string} accessory
     * @param {string} align
     * @param {string} version
     * @return {Object}
     */
    _getClosestVersion(size, accessory, align, version) {
        const versions = this.variants[size][accessory][align];
        const versionKeys = Object.keys(versions);

        const closestVersionKey = versionKeys.find(versionKey => new RegExp(`${version}_[0-9]+`).test(versionKey));

        if (closestVersionKey) {
            return versions[closestVersionKey];
        }

        return null;
    }

    /**
     * Returns variant for the latest version of the given options
     *
     * @param {number} size
     * @param {string} accessory
     * @param {string} align
     * @return {Object} Variant
     * @private
     */
    _getLatestVersion(size, accessory, align) {
        this._assertExists(size, accessory, align);

        const versions = this.variants[size][accessory][align];

        return max(versions, (version, key) => parseInt(key.replace('_', ''), 10));
    }

    /**
     * Throws an exception if variant with given parameters not exist.
     *
     * @param {number} [size]
     * @param {string} [accessory]
     * @param {string} [align]
     * @param {string} [version]
     * @private
     */
    _assertExists(size, accessory, align, version) {
        const hasNot = (obj, key) => Object.keys(obj).indexOf(String(key)) < 0;

        if (arguments.length > 0 && hasNot(this.variants, size)) {
            throw new Error(`Variant with size ${size} not found`);
        }

        if (arguments.length > 1 && hasNot(this.variants[size], accessory)) {
            throw new Error(`Variant with accessory ${accessory} not found`);
        }

        if (arguments.length > 2 && hasNot(this.variants[size][accessory], align)) {
            throw new Error(`Variant with align ${align} not found`);
        }

        if (arguments.length > 3 && hasNot(this.variants[size][accessory][align], version)) {
            throw new Error(`Variant with version ${version} not found`);
        }
    }

    /**
     * Checks if a variant with the given options exists
     *
     * @param {number} [size]
     * @param {string} [accessory]
     * @param {string} [align]
     * @param {string} [version]
     * @return {boolean} True if variant exists
     * @private
     */
    _exists(...args) {
        if (args.length > 3) {
            return !!this.get(...args);
        }
        try {
            this._assertExists(...args);
            return true;
        } catch (e) {
            return false;
        }
    }

    getItemIdFor(...args) {
        const product = this.get(...args);
        return product ? product.itemId : null;
    }

    getImageFor(...args) {
        const product = this.get(...args);
        return product ? product.image : null;
    }

    getAccessoryImageFor(...args) {
        const product = this.get(...args);
        return product ? product.accessoryImage : null;
    }

    getAccessoryProductNrFor(...args) {
        const product = this.get(...args);

        if (!product.accessory || !product.accessory.productNr) {
            return null;
        }

        return product.accessory.productNr;
    }

    getAccessoryTitleFor(...args) {
        const product = this.get(...args);

        if (!product.accessory || !product.accessory.productNr) {
            return null;
        }

        return product.accessory.title;
    }

    getAccessoryManufacturerFor(...args) {
        const product = this.get(...args);

        if (!product.accessory || !product.accessory.productNr) {
            return null;
        }

        return product.accessory.manufacturer;
    }

    /**
     * Returns array with available sizes for the given item. This method is
     * used to generate the size dropdown in
     * SizeField
     *
     * @param {ItemInterface} item
     * @return {Object[]}
     */
    getSizeOptions(item) {
        const sizes = [];
        const accessory = item.get('accessory');
        const align = item.get('align');
        const current = item.get('size');
        const sizeKeys = Object.keys(this.variants);

        sizeKeys.sort(naturalSort);

        sizeKeys.forEach((size) => {
            if (this._exists(size, accessory, align)) {
                const sizeItem = this.get(size, accessory, align);
                sizes.push({
                    key: this.getItemIdFor(size, accessory, align),
                    type: sizeItem.type,
                    value: size,
                    active: String(current) === size,
                    available: sizeItem.available || sizeItem.custom,
                });
            }
        });

        return sizes;
    }

    /**
     * Returns number of available sizes for the given item.
     *
     * @param {ItemInterface} item
     * @return {number}
     */
    countSizeOptions(item) {
        const accessory = item.get('accessory');
        const align = item.get('align');

        return Object
            .keys(this.variants)
            .reduce((count, size) => (this._exists(size, accessory, align) ? count + 1 : count), 0);
    }

    /**
     * Returns array with available accessories for the given item. This
     * method is used to generate the size dropdown in
     * AccessoryField
     *
     * @param {ItemInterface} item
     * @return {Object[]}
     */
    getAccessoryOptions(item) {
        const size = item.get('size');
        const align = item.get('align');
        const current = `${item.get('accessory')}`;
        const accessories = [];

        this._assertExists(size);

        const accessoryKeys = Object.keys(this.variants[size]);

        accessoryKeys.forEach((accessory) => {
            if (this._exists(size, accessory, align)) {
                accessories.push({
                    itemId: this.getItemIdFor(size, accessory, align),
                    key: accessory,
                    image: this.getImageFor(size, accessory, align),
                    accessoryImage: this.getAccessoryImageFor(size, accessory, align),
                    productNr: this.getAccessoryProductNrFor(size, accessory, align),
                    manufacturer: this.getAccessoryManufacturerFor(size, accessory, align),
                    title: this.getAccessoryTitleFor(size, accessory, align),
                    active: current === (`${accessory}`),
                });
            }
        });

        return accessories;
    }

    /**
     * Returns number of available accessories for the given item.
     *
     * @param {ItemInterface} item
     * @return {number}
     */
    getAccessoryCount(item) {
        const size = item.get('size');
        const align = item.get('align');

        this._assertExists(size);

        return Object
            .keys(this.variants[size])
            .reduce((count, accessory) => (this._exists(size, accessory, align) ? count + 1 : count), 0);
    }

    /**
     * Returns array with available alignments for the given item. This
     * method is used to generate the size dropdown in
     * AlignField
     *
     * @param {ItemInterface} item
     * @return {Object[]}
     */
    getAlignOptions(item) {
        const aligns = [];
        const size = item.get('size');
        const accessory = item.get('accessory');
        const current = item.get('align');

        // We ignore version for sets, because set alignments are created manually and are usually
        // not versioned properly, leading to layouts that were created on different days to not be available if we
        // were to take them into consideration. Normally scanned alignments on the other hand have the same version
        // for all alignments, thus we only return alignment options that match the current version.
        const version = item.get('type') !== 'SetGroup' ? item.get('version') : undefined;

        this._assertExists(size, accessory);

        const alignKeys = Object.keys(this.variants[size][accessory]);

        alignKeys.forEach((align) => {
            let alignItem;
            if (this._exists(size, accessory, align, version)) {
                alignItem = this.get(size, accessory, align, version);

                aligns.push({
                    key: this.getItemIdFor(size, accessory, align, version),
                    value: align,
                    active: current === align,
                    type: alignItem.type,
                    image: this.getImageFor(size, accessory, align),
                });
            }
        });

        return aligns;
    }

    /**
     * Returns number of available alignments for the given item.
     *
     * @param {ItemInterface} item
     * @return {number}
     */
    getAlignCount(item) {
        const size = item.get('size');
        const accessory = item.get('accessory');

        this._assertExists(size, accessory);

        return Object.keys(this.variants[size][accessory]).length;
    }

    /**
     * Returns array with available versions for the given item. This method
     * is used to generate the size dropdown in
     * VersionField
     *
     * @param {ItemInterface} item
     * @return {Object[]}
     */
    getVersionOptions(item) {
        const size = item.get('size');
        const accessory = item.get('accessory');
        const align = item.get('align');
        const current = item.get('itemId');

        this._assertExists(size, accessory, align);

        const versions = this.variants[size][accessory][align];

        return Object
            .keys(versions)
            .map((version) => {
                const data = versions[version];
                return {
                    itemId: data.itemId,
                    title: data.version,
                    type: data.type,
                    description: data.versionComment || '',
                    active: data.itemId === current,
                    image: this.getImageFor(size, accessory, align, version),
                };
            });
    }

    /**
     * Returns number of available versions for the given item.
     *
     * @param {ItemInterface} item
     * @return {number}
     */
    getVersionCount(item) {
        const size = item.get('size');
        const accessory = item.get('accessory');
        const align = item.get('align');

        this._assertExists(size, accessory, align);

        return Object.keys(this.variants[size][accessory][align]).length;
    }
}
